import { router } from "@inertiajs/react"
import type { DealDrawer as DealDrawerData } from "@/types/v2/conversation"
import type { DrawerProp } from "@/types/v2/drawer"
import { applyPreviewUpdate } from "@/lib/preview-prop-registry"

// The deal payload powering the v2 inline-edit hooks lives
// under different prop names depending on the host page:
//
// - `"deal"` — top-level prop on the deal show page
// - `"dealDrawer"` — legacy top-level prop on pages that
//   mount the deal slideover as its own component (still
//   used by the deals index drawer host)
// - `"drawer"` — the unified slideover prop. The deal data
//   lives nested at `drawer.active.data` when the active
//   trail entry is a deal.
// - `"preview"` — the command-palette rich-preview pane
//   (CON-183). There's no Inertia `drawer` prop to patch
//   there; the deal data lives in the pane's local state, so
//   the optimistic transform routes through the preview-prop
//   registry instead of `router.replaceProp`.
//
// Hooks call `replaceDealProp(propKey, transformer)` and
// don't need to know which shape the host page uses.

export type DealPropKey = "deal" | "dealDrawer" | "drawer" | "preview"

// Replace the current deal payload via the optimistic
// `router.replaceProp` mechanism. Drawer-hosted variants
// transform only the nested `active.data` field and leave
// the rest of the drawer prop intact.
export function replaceDealProp(
  propKey: DealPropKey,
  transformer: (current: DealDrawerData) => DealDrawerData,
) {
  if (propKey === "preview") {
    applyPreviewUpdate((d) => transformer(d as DealDrawerData) as unknown)
    return
  }

  if (propKey === "drawer") {
    router.replaceProp("drawer", (prev: DrawerProp | null) => {
      if (!prev?.active || prev.active.type !== "deal") return prev
      const updated = transformer(prev.active.data)
      return {
        ...prev,
        active: {
          ...prev.active,
          data: updated,
        },
      }
    })
    return
  }

  router.replaceProp(propKey, (prev: DealDrawerData | null) => {
    if (!prev) return prev
    return transformer(prev)
  })
}
